/**
 * "Two lines. One meeting."
 * The home page is two continuous strokes, the child's (lavender) and the parent's (navy),
 * each defined as a list of points in a 1000-unit-wide coordinate system and smoothed
 * with Catmull-Rom curves. Text and small overlays are placed at named anchors in the
 * same coordinates, so everything lines up at any screen width.
 */
export type Pt = [number, number];

export interface Anchors {
  heroText: { y: number };
  beats: {
    text: { x: number; y: number; w: number; align: 'start' | 'center' };
    y0: number;
  }[];
  quote: Pt;            // beat 1: the question below the curl
  curlExit: Pt;         // beat 1: where the curl finishes
  bubble: { x: number; y: number; w: number; h: number }; // beat 2: inside the bubble
  bubbleExit: Pt;       // beat 2: where the bubble's tail finishes
  milestones: Pt[];     // beat 3: dots on the child's rising line
  eyes: [Pt, Pt];       // beat 4: above the smile
  smileEnd: Pt;         // beat 4: where the smile finishes
  meet: { x: number; y: number; w: number };  // beat 5: the logo mark box (top-left, width)
  cta: { y: number };
  /** pen-y range over which the remaining stroke completes, so the meeting lands while the mark is high on screen */
  finish: { from: number; to: number };
}

export interface LineLayout {
  W: number;
  H: number;
  child: Pt[];
  parent: Pt[];
  anchors: Anchors;
  /** In Arabic the drawing is mirrored but the logo is not, so the last few points of each
   *  line (the approach to the mark) are replaced by these physical-coordinate tails. */
  rtl: { childTail: Pt[]; childTailCount: number; parentTail: Pt[]; parentTailCount: number };
}

/** Mirror a layout for right-to-left reading: every x becomes W - x, except the meeting. */
export const mirrorLayout = (l: LineLayout): LineLayout => {
  const mx = (p: Pt): Pt => [l.W - p[0], p[1]];
  const child = [...l.child.slice(0, l.child.length - l.rtl.childTailCount).map(mx), ...l.rtl.childTail];
  const parent = [...l.parent.slice(0, l.parent.length - l.rtl.parentTailCount).map(mx), ...l.rtl.parentTail];
  const a = l.anchors;
  const anchors: Anchors = {
    ...a,
    beats: a.beats.map((b) => ({ ...b, text: { ...b.text, x: l.W - b.text.x - b.text.w } })),
    quote: mx(a.quote),
    curlExit: mx(a.curlExit),
    bubble: { ...a.bubble, x: l.W - a.bubble.x - a.bubble.w },
    bubbleExit: mx(a.bubbleExit),
    milestones: a.milestones.map(mx),
    eyes: [mx(a.eyes[1]), mx(a.eyes[0])],
    smileEnd: mx(a.smileEnd),
  };
  return { ...l, child, parent, anchors };
};

/** Catmull-Rom spline through every point, emitted as cubic Béziers. */
export const smoothPath = (pts: Pt[], tension = 0.5): string => {
  if (pts.length < 2) return '';
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(i + 2, pts.length - 1)];
    const c1: Pt = [p1[0] + ((p2[0] - p0[0]) / 6) * tension * 2, p1[1] + ((p2[1] - p0[1]) / 6) * tension * 2];
    const c2: Pt = [p2[0] - ((p3[0] - p1[0]) / 6) * tension * 2, p2[1] - ((p3[1] - p1[1]) / 6) * tension * 2];
    d += ` C ${c1[0].toFixed(1)} ${c1[1].toFixed(1)}, ${c2[0].toFixed(1)} ${c2[1].toFixed(1)}, ${p2[0]} ${p2[1]}`;
  }
  return d;
};

/* ------------------------------------------------------------------ desktop */
// Lanes: the child's line lives on the left edge, the parent's on the right edge.
// In every beat one line makes a gesture while the other stays calm, and the text
// sits in whichever column is calm. The lines never cross until they meet.
const desktop = (): LineLayout => {
  const heroH = 640;
  const beatH = 720;
  const y = (i: number) => heroH + i * beatH;
  const H = heroH + 4 * beatH + 560;
  const MW = 44; // width of the logo mark at the meeting
  const meet = { x: 480, y: y(4) + 70, w: MW };
  const dotTop: Pt = [meet.x + 0.745 * MW, meet.y + 0.24 * MW]; // centre of the dot
  const dropTip: Pt = [meet.x + 0.42 * MW, meet.y + 2.6 * MW]; // bottom of the drop
  const rtl = {
    // child arrives from the right, straight into the dot
    childTail: [[665, y(3) + 460], [665, y(3) + 620], [600, y(4) - 30], [545, y(4) + 20], dotTop] as Pt[],
    childTailCount: 5,
    // parent arrives from the left, passes under the letters, and rises into the drop's tip
    parentTail: [[180, y(4) - 20], [190, y(4) + 130], [300, y(4) + 226], [460, y(4) + 222], dropTip] as Pt[],
    parentTailCount: 4,
  };

  const child: Pt[] = [
    [110, 260], [95, 520], [85, 800], [100, y(0) + 420], [150, y(1) - 80],
    // beat 2: the speech bubble, entered at its top-left, left through the tail
    [120, y(1) + 60], [160, y(1) + 120], [300, y(1) + 120], [350, y(1) + 150], [360, y(1) + 240], [340, y(1) + 320],
    [280, y(1) + 340], [170, y(1) + 340], [125, y(1) + 370], [105, y(1) + 430], [125, y(1) + 520],
    // beat 3: down to the base, then the rise to a peak and gently back down
    [130, y(2) + 60], [110, y(2) + 260], [100, y(2) + 430], [130, y(2) + 520],
    [240, y(2) + 470], [350, y(2) + 370], [440, y(2) + 300], [510, y(2) + 240],
    [560, y(2) + 270], [560, y(2) + 380], [490, y(2) + 520], [380, y(2) + 640],
    // beat 4: in from the left, then the smile
    [260, y(3) + 80], [150, y(3) + 150], [95, y(3) + 230], [100, y(3) + 300],
    [125, y(3) + 375], [180, y(3) + 405], [245, y(3) + 405], [300, y(3) + 375], [325, y(3) + 300],
    // beat 5: to the top of the dot
    [335, y(3) + 460], [335, y(3) + 620], [420, y(4) - 30], [490, y(4) + 30], dotTop,
  ];

  const parent: Pt[] = [
    [890, 260], [905, 520], [880, 720],
    // beat 1: a curl of thought, then down the far right
    [830, y(0) + 180], [720, y(0) + 250], [650, y(0) + 200], [665, y(0) + 125], [745, y(0) + 110], [800, y(0) + 190],
    [770, y(0) + 330], [840, y(0) + 480], [900, y(0) + 640],
    // beat 2: calm on the right
    [910, y(1) + 150], [920, y(1) + 400], [910, y(1) + 640],
    // beat 3: the parent's own smaller rise, mirrored
    [890, y(2) + 120], [880, y(2) + 330], [830, y(2) + 480], [730, y(2) + 450], [660, y(2) + 360],
    [630, y(2) + 400], [660, y(2) + 540], [760, y(2) + 650],
    // beat 4: calm on the right
    [880, y(3) + 120], [920, y(3) + 400], [900, y(3) + 640],
    // beat 5: round to the tip of the drop
    [820, y(4) - 20], [690, y(4) + 110], [570, y(4) + 200], dropTip,
  ];

  const anchors: Anchors = {
    heroText: { y: 300 },
    beats: [
      { y0: y(0), text: { x: 160, y: y(0) + 130, w: 380, align: 'start' } },
      { y0: y(1), text: { x: 480, y: y(1) + 150, w: 380, align: 'start' } },
      { y0: y(2), text: { x: 250, y: y(2) + 20, w: 500, align: 'center' } },
      { y0: y(3), text: { x: 480, y: y(3) + 180, w: 380, align: 'start' } },
      { y0: y(4), text: { x: 250, y: y(4) + 262, w: 500, align: 'center' } },
    ],
    quote: [640, y(0) + 560],
    curlExit: [770, y(0) + 330],
    bubble: { x: 165, y: y(1) + 150, w: 175, h: 170 },
    bubbleExit: [125, y(1) + 520],
    milestones: [[240, y(2) + 470], [350, y(2) + 370], [440, y(2) + 300]],
    eyes: [[160, y(3) + 255], [270, y(3) + 255]],
    smileEnd: [325, y(3) + 300],
    meet,
    cta: { y: y(4) + 452 },
    finish: { from: meet.y - 200, to: meet.y + 200 },
  };

  return { W: 1000, H, child, parent, anchors, rtl };
};

/* ------------------------------------------------------------------- mobile */
const mobile = (): LineLayout => {
  const heroH = 1900;
  const beatH = 2150;
  const y = (i: number) => heroH + i * beatH;
  const H = heroH + 4 * beatH + 1600;
  const MW = 90;
  const meet = { x: 455, y: y(4) + 120, w: MW };
  const dotTop: Pt = [meet.x + 0.745 * MW, meet.y + 0.24 * MW]; // centre of the dot
  const dropTip: Pt = [meet.x + 0.42 * MW, meet.y + 2.6 * MW]; // bottom of the drop
  const rtl = {
    childTail: [[520, y(3) + 1600], [560, y(3) + 1950], [580, y(4) + 60], dotTop] as Pt[],
    childTailCount: 4,
    parentTail: [[120, y(4) + 60], [100, y(4) + 400], [200, y(4) + 540], [420, y(4) + 520], dropTip] as Pt[],
    parentTailCount: 4,
  };
  // text sits in the top ~850 units of each beat, the gesture below it
  const child: Pt[] = [
    [150, 700], [120, 1100], [110, 1500], [120, y(0) + 400], [110, y(0) + 1250], [120, y(0) + 1850],
    [140, y(1) + 1130], [190, y(1) + 1210], [520, y(1) + 1210], [600, y(1) + 1250], [610, y(1) + 1360], [590, y(1) + 1460],
    [520, y(1) + 1490], [250, y(1) + 1490], [180, y(1) + 1520], [160, y(1) + 1600], [180, y(1) + 1720],
    [150, y(2) + 150], [110, y(2) + 550], [110, y(2) + 1200], [130, y(2) + 1500], [140, y(2) + 1670],
    [270, y(2) + 1580], [390, y(2) + 1460], [490, y(2) + 1370], [550, y(2) + 1320], [590, y(2) + 1370], [570, y(2) + 1480],
    [480, y(2) + 1680], [360, y(2) + 1950], [200, y(3) + 200], [120, y(3) + 600], [110, y(3) + 1200], [130, y(3) + 1330],
    [170, y(3) + 1450], [250, y(3) + 1500], [350, y(3) + 1500], [430, y(3) + 1450], [470, y(3) + 1330],
    [480, y(3) + 1600], [450, y(3) + 1950], [480, y(4) + 20], dotTop,
  ];
  const parent: Pt[] = [
    [850, 700], [880, 1100], [890, 1500], [870, y(0) + 400],
    [820, y(0) + 1250], [700, y(0) + 1330], [630, y(0) + 1260], [650, y(0) + 1170], [740, y(0) + 1150], [800, y(0) + 1240],
    [790, y(0) + 1400], [860, y(0) + 1600], [900, y(0) + 1850],
    [900, y(1) + 400], [910, y(1) + 1250], [900, y(1) + 1850],
    [890, y(2) + 400], [890, y(2) + 1200], [880, y(2) + 1670], [780, y(2) + 1580], [680, y(2) + 1470], [630, y(2) + 1460], [640, y(2) + 1600], [720, y(2) + 1900],
    [890, y(3) + 400], [910, y(3) + 1250], [900, y(3) + 1850],
    [800, y(4) + 60], [660, y(4) + 320], [560, y(4) + 400], dropTip,
  ];
  const anchors: Anchors = {
    heroText: { y: 900 },
    beats: [0, 1, 2, 3].map((i) => ({ y0: y(i), text: { x: 170, y: y(i) + 130, w: 660, align: 'start' as const } })),
    quote: [500, y(0) + 1700],
    curlExit: [790, y(0) + 1400],
    bubble: { x: 210, y: y(1) + 1240, w: 370, h: 230 },
    bubbleExit: [180, y(1) + 1720],
    milestones: [[270, y(2) + 1580], [390, y(2) + 1460], [490, y(2) + 1370]],
    eyes: [[210, y(3) + 1260], [390, y(3) + 1260]],
    smileEnd: [470, y(3) + 1330],
    meet,
    cta: { y: y(4) + 1400 },
    finish: { from: meet.y - 300, to: meet.y + 700 },
  };
  anchors.beats.push({ y0: y(4), text: { x: 120, y: y(4) + 480, w: 760, align: 'center' } });
  return { W: 1000, H, child, parent, anchors, rtl };
};

export const LAYOUTS = { desktop: desktop(), mobile: mobile() };
